import React, { useEffect } from 'react';

import {
  SidebarProvider,
  SidebarInset,
  useSidebar,
} from '@/design-system/sidebar';
import { Sidebar } from './Sidebar';
import { LayoutProps } from '@/types/components';
import { storage } from '@/utils/storage';

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

/**
 * Below this the sidebar auto-collapses to the icon rail. 1280x720 is the
 * floor for a comfortable expanded sidebar — under it the 240px column costs
 * more than it gives back. This sits above Tailwind's `md` (768px), so phones
 * fall inside it too and still get the drawer + scrim treatment, which is
 * pure CSS (`md:hidden`) and keys off `md` independently.
 */
const AUTO_COLLAPSE_WIDTH = 1280;
const AUTO_COLLAPSE_HEIGHT = 720;

const AUTO_COLLAPSE_QUERY = `(max-width: ${AUTO_COLLAPSE_WIDTH - 1}px), (max-height: ${AUTO_COLLAPSE_HEIGHT - 1}px)`;

/**
 * A zero dimension means the document isn't laid out yet (background tab,
 * hidden pane, prerender) — that's "unknown", not "small". Reporting it as
 * small would force-collapse and skip persistence on a full-size desktop.
 */
const isBelowAutoCollapse = () => {
  if (typeof window === 'undefined') return false;
  const { innerWidth: width, innerHeight: height } = window;
  if (width === 0 || height === 0) return false;
  return width < AUTO_COLLAPSE_WIDTH || height < AUTO_COLLAPSE_HEIGHT;
};

const getInitialSidebarState = (): 'expanded' | 'collapsed' => {
  // Small viewports always start collapsed, overriding the stored preference.
  if (isBelowAutoCollapse()) return 'collapsed';
  return storage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    ? 'collapsed'
    : 'expanded';
};

const LayoutShell: React.FC<LayoutProps> = ({ children, currentPath = '/' }) => {
  const { state, setState, toggle } = useSidebar();
  const isCollapsed = state === 'collapsed';

  // Persist the full-size preference only. Auto-collapsing on a small
  // viewport is a constraint, not a choice, so writing it would stomp what
  // the user actually picked on a large screen.
  useEffect(() => {
    if (isBelowAutoCollapse()) return;
    storage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  // Collapse when the viewport crosses below the threshold. One-way on
  // purpose: growing the window back does not re-expand, so a deliberate
  // collapse survives an incidental resize.
  useEffect(() => {
    const mql = window.matchMedia(AUTO_COLLAPSE_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setState('collapsed');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [setState]);

  return (
    <>
      <Sidebar currentPath={currentPath} />

      <SidebarInset>
        <header className="bg-bg-white-0 border-b border-stroke-soft-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="label-eyebrow tabular">
                <span className="text-accent font-bold mr-3">·</span>
                Dash · Logistic
              </span>
            </div>

            <div className="flex items-center space-x-4"></div>
          </div>
        </header>

        <main className="flex-1 overflow-auto">{children}</main>
      </SidebarInset>

      {!isCollapsed && (
        <div
          className="fixed inset-0 bg-[#171717]/45 z-40 md:hidden"
          onClick={toggle}
        />
      )}
    </>
  );
};

export const Layout: React.FC<LayoutProps> = ({ children, currentPath }) => (
  <SidebarProvider
    defaultState={getInitialSidebarState()}
    className="h-screen bg-bg-weak-50 overflow-hidden"
  >
    <LayoutShell currentPath={currentPath}>{children}</LayoutShell>
  </SidebarProvider>
);
